/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  MCPSetupGuideRemote,
  MCPSetupGuideRemote$inboundSchema,
} from "./mcpsetupguideremote.js";

/**
 * How the lookup matched this guide. The most specific kind, when both lookup keys matched it.
 */
export const MatchKind = {
  Endpoint: "endpoint",
  Alias: "alias",
} as const;
/**
 * How the lookup matched this guide. The most specific kind, when both lookup keys matched it.
 */
export type MatchKind = ClosedEnum<typeof MatchKind>;

/**
 * Published setup documentation for an upstream MCP server
 */
export type MCPSetupGuide = {
  /**
   * How the server is meant to be added in Speakeasy, when the guide states one (e.g., 'catalog', 'custom-remote')
   */
  addServerFlow?: string | undefined;
  /**
   * Registry identifiers the guide is also published under
   */
  aliases: Array<string>;
  /**
   * Markdown instructions for the setup work that happens in the upstream provider
   */
  externalMarkdown: string;
  /**
   * How the lookup matched this guide. The most specific kind, when both lookup keys matched it.
   */
  matchKind: MatchKind;
  /**
   * ID of the documented endpoint the lookup matched. Absent when the lookup identified the guide and not a specific endpoint, which is always the case for an 'alias' match.
   */
  matchedRemoteId?: string | undefined;
  /**
   * Endpoints documented by the guide
   */
  remotes: Array<MCPSetupGuideRemote>;
  /**
   * Stable identifier of the guide
   */
  slug: string;
  /**
   * Markdown instructions for the setup work that happens in Speakeasy
   */
  speakeasyMarkdown: string;
  /**
   * One-line summary of what the guide covers
   */
  summary: string;
  /**
   * Display title of the guide
   */
  title: string;
};

/** @internal */
export const MatchKind$inboundSchema: z.ZodMiniEnum<typeof MatchKind> = z.enum(
  MatchKind,
);

/** @internal */
export const MCPSetupGuide$inboundSchema: z.ZodMiniType<
  MCPSetupGuide,
  unknown
> = z.pipe(
  z.object({
    add_server_flow: z.optional(z.string()),
    aliases: z.array(z.string()),
    external_markdown: z.string(),
    match_kind: MatchKind$inboundSchema,
    matched_remote_id: z.optional(z.string()),
    remotes: z.array(MCPSetupGuideRemote$inboundSchema),
    slug: z.string(),
    speakeasy_markdown: z.string(),
    summary: z.string(),
    title: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "add_server_flow": "addServerFlow",
      "external_markdown": "externalMarkdown",
      "match_kind": "matchKind",
      "matched_remote_id": "matchedRemoteId",
      "speakeasy_markdown": "speakeasyMarkdown",
    });
  }),
);

export function mcpSetupGuideFromJSON(
  jsonString: string,
): SafeParseResult<MCPSetupGuide, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => MCPSetupGuide$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'MCPSetupGuide' from JSON`,
  );
}
